import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import SolidStarCircleIcon from 'LuxKit/Icons/solid/SolidStarCircleIcon'
import TextLink from 'LuxKit/TextLink'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { diffInDays } from 'lib/datetime/dateUtils'
import { hasLivePromotionRates } from 'selectors/offerPage/propertyOfferPageSelectors'

function deriveNewCheckOutIsoDate(checkOutIsoDate: string | undefined, durationShortfall: number) {
  if (!checkOutIsoDate || !durationShortfall) return checkOutIsoDate
  const newCheckOutDate = new Date(checkOutIsoDate)
  newCheckOutDate.setUTCDate(newCheckOutDate.getUTCDate() + durationShortfall)
  return newCheckOutDate.toISOString().substring(0, 10)
}

function getMessageBannerIcon(promotion: Pick<App.Property.PropertyPromotion, 'type'>) {
  if (promotion.type === 'dynamicFlash') return <SolidStarCircleIcon colour="exclusive" />
  return <SolidFireIcon />
}

function getAwarenessMessageBannerContent(
  intl: IntlShape,
  promotion: Pick<App.Property.PropertyPromotion, 'type' | 'minimumLos'>,
) {
  if (promotion.type === 'dynamicFlash') {
    return intl.formatMessage({
      id: 'Ze-N2OOoBFOb244O1hnB',
      defaultMessage: 'Limited time exclusive packages available when you stay {minimumNights, plural, one {#+ night} other {#+ nights}} on select dates.',
      description: 'Message banner content informing customers that they may see "limited time exclusive" rates if they meet certain booking conditions',
    }, {
      minimumNights: promotion.minimumLos,
    })
  }
  return intl.formatMessage({
    id: 'jdiZSJBmot9VrJrOpT1C',
    defaultMessage: 'Limited time special packages available when you stay {minimumNights, plural, one {#+ night} other {#+ nights}} on select dates.',
    description: 'Message banner content informing customers that they may see "limited time special" rates if they meet certain booking conditions',
  }, {
    minimumNights: promotion.minimumLos,
  })
}

function getUpsellMessageBannerContent(
  intl: IntlShape,
  promotion: Pick<App.Property.PropertyPromotion, 'type'>,
  durationShortfall: number,
  onCtaClick: () => void,
) {
  if (promotion.type === 'dynamicFlash') {
    return intl.formatMessage({
      id: 'TAUN-_hDcMUJr-ljGlHs',
      defaultMessage: 'Stay {extraNights, plural, one {# more night} other {# more nights}} to unlock limited time exclusive packages. · <cta>Extend stay {extraNights, plural, one {# night} other {# nights}}</cta>',
      description: 'Message banner content and CTA informing customers that they would meet the booking conditions to see "limited time exclusive" rates if they extend their stay by a number of extra nights',
    }, {
      extraNights: durationShortfall,
      cta: (chunks) => <TextLink weight="regular" onClick={onCtaClick}>{chunks}</TextLink>,
    })
  }
  return intl.formatMessage({
    id: 'ZzAYX4PEy344jfW4iRP8',
    defaultMessage: 'Stay {extraNights, plural, one {# more night} other {# more nights}} to unlock limited time special packages. · <cta>Extend stay {extraNights, plural, one {# night} other {# nights}}</cta>',
    description: 'Message banner content and CTA informing customers that they would meet the booking conditions to see "limited time special" rates if they extend their stay by a number of extra nights',
  }, {
    extraNights: durationShortfall,
    cta: (chunks) => <TextLink weight="regular" onClick={onCtaClick}>{chunks}</TextLink>,
  })
}

function getConfirmationMessageBannerContent(
  intl: IntlShape,
  promotion: Pick<App.Property.PropertyPromotion, 'type'>,
) {
  if (promotion.type === 'dynamicFlash') {
    return intl.formatMessage({
      id: 'XrTpFlxDfI03TWv43ood',
      defaultMessage: 'Limited time exclusive packages available for your selected dates!',
      description: 'Message banner content confirming to customers that they have met the booking conditions to see "limited time exclusive" rates',
    })
  }
  return intl.formatMessage({
    id: '7vhxeGJOG0ljJrS5jbMu',
    defaultMessage: 'Limited time special packages available for your selected dates!',
    description: 'Message banner content confirming to customers that they have met the booking conditions to see "limited time special" rates',
  })
}

interface Props {
  promotion: App.Property.PropertyPromotion | undefined
  checkInIsoDate: string | undefined
  checkOutIsoDate: string | undefined
  onExtendStay?: (newCheckOutIsoDate: string) => void
  canShowConfirmation: boolean
}

export function PropertyPromotionNudgeBanner({
  promotion,
  checkInIsoDate,
  checkOutIsoDate,
  onExtendStay,
  canShowConfirmation,
}: Props) {
  const intl = useIntl()

  if (!promotion) return null
  const isWithinTravelPeriod = (!!checkInIsoDate && checkInIsoDate >= promotion.travelFromIsoDate) && (!!checkOutIsoDate && checkOutIsoDate <= promotion.travelToIsoDate)
  const duration = (checkInIsoDate && checkOutIsoDate && checkInIsoDate < checkOutIsoDate) ? diffInDays(new Date(checkInIsoDate), new Date(checkOutIsoDate)) : 1
  const isAboveMinimumLos = duration >= promotion.minimumLos
  const durationShortfall = isAboveMinimumLos ? 0 : promotion.minimumLos - duration
  const newCheckOutIsoDate = deriveNewCheckOutIsoDate(checkOutIsoDate, durationShortfall)
  const shouldUpsellDuration = isWithinTravelPeriod && !isAboveMinimumLos && !!onExtendStay && (!!newCheckOutIsoDate && newCheckOutIsoDate <= promotion.travelToIsoDate)
  const onExtendStayCtaClick = () => {
    if (!shouldUpsellDuration) return
    onExtendStay(newCheckOutIsoDate)
  }

  const messageType = (!isWithinTravelPeriod || (!isAboveMinimumLos && !shouldUpsellDuration)) ? 'awareness' : shouldUpsellDuration ? 'upsell' : 'confirmation'

  if (messageType === 'confirmation' && !canShowConfirmation) return null
  return <MessageBanner
    kind="informational"
    icon={getMessageBannerIcon(promotion)}
  >
    {messageType === 'awareness' && getAwarenessMessageBannerContent(intl, promotion)}
    {messageType === 'upsell' && getUpsellMessageBannerContent(intl, promotion, durationShortfall, onExtendStayCtaClick)}
    {messageType === 'confirmation' && getConfirmationMessageBannerContent(intl, promotion)}
  </MessageBanner>
}

type MappedProps = Pick<Props, 'promotion' | 'checkInIsoDate' | 'checkOutIsoDate' | 'canShowConfirmation'>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  promotion: state.property?.promotion,
  checkInIsoDate: state.checkInDate,
  checkOutIsoDate: state.checkOutDate,
  canShowConfirmation: hasLivePromotionRates(state, appState),
}), PropertyPromotionNudgeBanner)
